import { sidebarCustomersOptions, sidebarEntriesOptions } from "@/api/queryApi";
import type { SidebarEntry } from "@/api/sidebarEntryAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmarkLarge } from "@fortawesome/sharp-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Button, cn, PageLayoutMobileHeaderContext } from "frontend-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import { SelectedCustomerContext } from "./selectedCustomerContext";
import UserMenu from "./UserMenu";
import { SidebarRefreshContext } from "./sidebarRefreshContext";
import { DashboardCustomizationProvider } from "@dashboard/contexts/DashboardCustomizationProvider";

const SIDEBAR_ID = "hub-sidebar";

export default function Hub({ customerId }: { customerId: number }) {
    const { t } = useTranslation();
    const sidebarQuery = useQuery(sidebarEntriesOptions(customerId));
    const sidebarCustomersQuery = useQuery(sidebarCustomersOptions());
    const { refetch: refetchSidebar } = sidebarQuery;
    const activeCustomerId = sidebarQuery.data?.selectedCustomer.id ?? customerId;
    const dashboardEntry = sidebarQuery.data?.entries.find(
        (entry): entry is Extract<SidebarEntry, { type: "DASHBOARD" }> =>
            entry.type === "DASHBOARD",
    );
    const isProduction = location.host === "app.compendia.no";

    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [titleRoot, setTitleRoot] = useState<HTMLElement | null>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const [lastPathname, setLastPathname] = useState(pathname);
    if (pathname !== lastPathname) {
        setLastPathname(pathname);
        setIsSidebarOpen(false);
    }

    const closeSidebar = useCallback(() => {
        setIsSidebarOpen(false);
        menuButtonRef.current?.focus();
    }, []);
    const refreshSidebar = useCallback(() => refetchSidebar(), [refetchSidebar]);

    useEffect(() => {
        if (!isSidebarOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeSidebar();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isSidebarOpen, closeSidebar]);

    return (
        <SidebarRefreshContext.Provider value={{ refreshSidebar }}>
            <DashboardCustomizationProvider>
                <div className="flex h-screen w-screen flex-col overflow-hidden">
                    {!isProduction && (
                        <div className="bg-chameleon-pink flex flex-row justify-center text-white">
                            {t("hub.testEnvironmentWarning")}
                        </div>
                    )}
                    <header className="border-border bg-background flex h-14 shrink-0 items-center gap-3 border-b px-4 lg:hidden">
                        <Button
                            ref={menuButtonRef}
                            type="button"
                            variant="ghost"
                            aria-label={isSidebarOpen ? t("hub.closeMenu") : t("hub.openMenu")}
                            aria-expanded={isSidebarOpen}
                            aria-controls={SIDEBAR_ID}
                            onClick={() => setIsSidebarOpen((open) => !open)}
                            className="hover:bg-muted focus-visible:ring-outline flex size-9 shrink-0 items-center justify-center border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
                        >
                            <FontAwesomeIcon
                                icon={isSidebarOpen ? faXmarkLarge : faBars}
                                className="size-4"
                            />
                        </Button>
                        <div
                            ref={setTitleRoot}
                            className="flex min-w-0 flex-1 flex-col justify-center [&_h1]:text-lg"
                        />
                        <UserMenu variant="compact" />
                    </header>
                    <div className="bg-card text-foreground relative flex min-h-0 flex-1 overflow-hidden">
                        <Sidebar
                            id={SIDEBAR_ID}
                            className={cn(
                                "invisible absolute inset-y-0 left-0 z-20 shadow-xl transition-[translate,visibility] duration-300 ease-in-out lg:visible lg:static lg:z-auto lg:translate-x-0 lg:shadow-none lg:transition-none",
                                isSidebarOpen ? "visible translate-x-0" : "-translate-x-full",
                            )}
                            sidebarQuery={sidebarQuery}
                            sidebarCustomersQuery={sidebarCustomersQuery}
                        />
                        <div
                            aria-hidden
                            onClick={closeSidebar}
                            className={cn(
                                "absolute inset-0 z-10 bg-black/30 transition-opacity duration-300 lg:hidden",
                                isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0",
                            )}
                        />
                        <main className="bg-card isolate flex min-w-0 flex-1 overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]">
                            <SelectedCustomerContext.Provider
                                value={{
                                    selectedCustomerId: activeCustomerId,
                                    dashboardNavigation: dashboardEntry?.navigation,
                                    isDashboardNavigationPending: sidebarQuery.isPending,
                                }}
                            >
                                <PageLayoutMobileHeaderContext.Provider value={titleRoot}>
                                    <Outlet />
                                </PageLayoutMobileHeaderContext.Provider>
                            </SelectedCustomerContext.Provider>
                        </main>
                    </div>
                </div>
            </DashboardCustomizationProvider>
        </SidebarRefreshContext.Provider>
    );
}
